import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { 
  Users, 
  TrendingUp, 
  Settings,
  MessageSquare,
  MapPin,
  BarChart3,
  Shield,
  BookOpen,
  Brain,
  PieChart,
  Target,
  Activity
} from 'lucide-react';
import { formatShortcut } from '@/hooks/useKeyboardShortcuts';

interface AdminSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isSpanish: boolean;
}

interface MenuItem {
  id: string;
  label: string;
  icon: any;
  shortcut?: string;
  link?: string;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  activeSection, 
  onSectionChange, 
  isSpanish 
}) => {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const location = useLocation();

  const menuGroups = [
    {
      label: isSpanish ? 'Resumen' : 'Overview',
      items: [
        { 
          id: 'summary', 
          label: isSpanish ? 'Resumen' : 'Summary', 
          icon: BarChart3,
          shortcut: '1'
        },
        { 
          id: 'analytics', 
          label: isSpanish ? 'Analíticas' : 'Analytics', 
          icon: PieChart,
          shortcut: '2'
        },
        { 
          id: 'usage', 
          label: isSpanish ? 'Analítica de Uso' : 'Usage Analytics', 
          icon: TrendingUp,
          shortcut: '3'
        }
      ]
    },
    {
      label: isSpanish ? 'Gestión' : 'Management',
      items: [
        { 
          id: 'users', 
          label: isSpanish ? 'Usuarios' : 'Users', 
          icon: Users,
          shortcut: '4'
        },
        { 
          id: 'reports', 
          label: isSpanish ? 'Reportes de Frontera' : 'Border Reports', 
          icon: MapPin,
          shortcut: '5'
        },
        { 
          id: 'points', 
          label: isSpanish ? 'Puntos de Recompensa' : 'Reward Points', 
          icon: TrendingUp,
          shortcut: '6'
        }
      ]
    },
    {
      label: isSpanish ? 'Publicidad' : 'Advertising',
      items: [
        { 
          id: 'advertisers', 
          label: isSpanish ? 'Anunciantes' : 'Advertisers', 
          icon: TrendingUp,
          shortcut: '7'
        },
        { 
          id: 'promos-management', 
          label: isSpanish ? 'Gestión de Promos' : 'Promos Management', 
          icon: Target,
          shortcut: '8'
        }
      ]
    },
    {
      label: isSpanish ? 'Contenido & IA' : 'Content & AI',
      items: [
        { 
          id: 'messages', 
          label: isSpanish ? 'Mensajes Diarios' : 'Daily Messages', 
          icon: MessageSquare,
          shortcut: '9'
        },
        { 
          id: 'madison-kb', 
          label: isSpanish ? 'Madison KB' : 'Madison KB', 
          icon: BookOpen
        },
        { 
          id: 'ai-settings', 
          label: isSpanish ? 'Configuración IA' : 'AI Settings', 
          icon: Brain
        }
      ]
    },
    {
      label: isSpanish ? 'Sistema' : 'System',
      items: [
        { 
          id: 'monitoring', 
          label: isSpanish ? 'Monitoreo' : 'Monitoring', 
          icon: Activity,
          link: '/admin/monitoring'
        },
        { 
          id: 'settings', 
          label: isSpanish ? 'Configuración' : 'Settings', 
          icon: Settings
        },
        { 
          id: 'tracking-summary', 
          label: isSpanish ? 'Resumen de Seguimiento' : 'Tracking Summary', 
          icon: Target
        },
        { 
          id: 'security', 
          label: isSpanish ? 'Seguridad' : 'Security', 
          icon: Shield
        }
      ]
    }
  ];

  const isActive = (id: string) => activeSection === id;

  return (
    <Sidebar
      className={isCollapsed ? "w-16" : "w-72"}
    >
      <div className="p-4 border-b border-sidebar-border flex items-center justify-between">
        {!isCollapsed && (
          <h2 className="text-lg font-semibold text-sidebar-foreground">
            {isSpanish ? 'Panel Admin' : 'Admin Panel'}
          </h2>
        )}
        <SidebarTrigger className="ml-auto" />
      </div>
      
      <SidebarContent>
        {menuGroups.map((group, groupIndex) => (
          <SidebarGroup key={groupIndex}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  
                  // If item has a link, use NavLink for routing
                  if (item.link) {
                    return (
                      <SidebarMenuItem key={item.id}>
                        <NavLink to={item.link} className="w-full">
                          {({ isActive: isLinkActive }) => (
                            <SidebarMenuButton
                              isActive={isLinkActive}
                              tooltip={isCollapsed ? item.label : undefined}
                            >
                              <Icon className="w-4 h-4" />
                              {!isCollapsed && (
                                <div className="flex-1 flex items-center justify-between">
                                  <span>{item.label}</span>
                                  {item.shortcut && (
                                    <span className="text-xs text-muted-foreground opacity-60">
                                      {item.shortcut}
                                    </span>
                                  )}
                                </div>
                              )}
                            </SidebarMenuButton>
                          )}
                        </NavLink>
                      </SidebarMenuItem>
                    );
                  }
                  
                  // Otherwise use onClick handler
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => onSectionChange(item.id)}
                        isActive={isActive(item.id)}
                        tooltip={isCollapsed ? item.label : undefined}
                      >
                        <Icon className="w-4 h-4" />
                        {!isCollapsed && (
                          <div className="flex-1 flex items-center justify-between">
                            <span>{item.label}</span>
                            {item.shortcut && (
                              <span className="text-xs text-muted-foreground opacity-60">
                                {item.shortcut}
                              </span>
                            )}
                          </div>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

export default AdminSidebar;
